import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PostLoginWrapper } from './PostLoginWrapper';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { addLocale } from 'primereact/api';

export const EditField = () => {
  const { id: fieldId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { field: fieldFromState } = location.state || {};
  
  const [field, setField] = useState(fieldFromState);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Estados para los campos editables
  const [fieldName, setFieldName] = useState('');
  const [porcentajeArena, setPorcentajeArena] = useState(50);
  const [porcentajeArcilla, setPorcentajeArcilla] = useState(20);
  const [porcentajeLimo, setPorcentajeLimo] = useState(30);
  const [profundidadRaices, setProfundidadRaices] = useState(0.65);
  const [potencialHidricoInicioEstres, setPotencialHidricoInicioEstres] = useState(2);
  const [potencialHidricoMaximoEstres, setPotencialHidricoMaximoEstres] = useState(3.2);
  const [distanciaFilas, setDistanciaFilas] = useState(2.4);
  const [distanciaPlantas, setDistanciaPlantas] = useState(1.2);
  const [coeficienteCorreccionEt0, setCoeficienteCorreccionEt0] = useState(1.032);
  const [kEscorrentia, setKEscorrentia] = useState(0.5);

  // Cargar datos del campo al montar el componente
  useEffect(() => {
    // Si tenemos el campo desde el state, usarlo directamente sin llamar al servidor
    if (fieldFromState && fieldFromState.id) {
      setField(fieldFromState);
      initializeFormData(fieldFromState);
      return;
    }

    // Solo cargar desde el servidor si no tenemos el field en el state
    const loadFieldData = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          navigate('/login');
          return;
        }

        const response = await fetch('/vitisad/get_campos', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data && Array.isArray(data.data)) {
            const targetField = data.data.find(f => f.id.toString() === fieldId);
            if (targetField) {
              setField(targetField);
              initializeFormData(targetField);
            } else {
              setErrorMessage('Campo no encontrado');
            }
          }
        } else if (response.status === 401) {
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            localStorage.setItem('accessToken', newAccessToken);
            loadFieldData();
          } else {
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Error cargando datos del campo:', error);
        setErrorMessage('Error al cargar los datos del campo');
      }
    };

    if (fieldId && !fieldFromState) {
      loadFieldData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId]);

  const initializeFormData = (fieldData) => {
    setFieldName(fieldData.nombre || '');
    setPorcentajeArena(fieldData.porcentaje_arena ?? 50);
    setPorcentajeLimo(fieldData.porcentaje_limo ?? 30);
    // Arcilla se derivará de arena + limo al mostrar/guardar
    setPorcentajeArcilla(fieldData.porcentaje_arcilla ?? 20);
    setProfundidadRaices(fieldData.profundidad_raices ?? 0.65);
    setPotencialHidricoInicioEstres(fieldData.potencial_hidrico_inicio_estres ?? 2);
    setPotencialHidricoMaximoEstres(fieldData.potencial_hidrico_maximo_estres ?? 3.2);
    setDistanciaFilas(fieldData.distancia_filas ?? 2.4);
    setDistanciaPlantas(fieldData.distancia_plantas ?? 1.2);
    setCoeficienteCorreccionEt0(fieldData.coeficiente_correccion_et0 ?? 1.032);
    setKEscorrentia(fieldData.k_escorrentia ?? 0.5);
  };

  // Arcilla derivada de arena + limo (como en CreateField)
  const porcentajeArcillaCalculado = Math.max(
    0,
    100 - (porcentajeArena || 0) - (porcentajeLimo || 0)
  );

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await fetch('/vitisad/refresh_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.accessToken;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error al renovar el token de acceso:", error);
      return null;
    }
  };

  const validateForm = () => {
    setErrorMessage('');
    
    if (fieldName.trim() === '') {
      setErrorMessage('El nombre del campo no puede estar vacío');
      return false;
    }

    // No permitir campos numéricos vacíos
    const numericFields = [
      { value: porcentajeArena, nombre: 'Porcentaje de arena' },
      { value: porcentajeLimo, nombre: 'Porcentaje de limo' },
      { value: profundidadRaices, nombre: 'Profundidad de raíces' },
      { value: potencialHidricoInicioEstres, nombre: 'Potencial hídrico inicio estrés' },
      { value: potencialHidricoMaximoEstres, nombre: 'Potencial hídrico máximo estrés' },
      { value: distanciaFilas, nombre: 'Distancia entre filas' },
      { value: distanciaPlantas, nombre: 'Distancia entre plantas' },
      { value: coeficienteCorreccionEt0, nombre: 'Coeficiente corrección ET0' },
      { value: kEscorrentia, nombre: 'Coeficiente de escorrentía' },
    ];

    const campoVacio = numericFields.find(
      (f) => f.value === null || f.value === undefined || f.value === ''
    );

    if (campoVacio) {
      setErrorMessage(`El campo "${campoVacio.nombre}" no puede estar vacío`);
      return false;
    }

    // La suma de arena y limo no puede superar 100
    if (
      porcentajeArena != null &&
      porcentajeLimo != null &&
      porcentajeArena + porcentajeLimo > 100
    ) {
      setErrorMessage('La suma de porcentaje de arena y limo no puede ser mayor que 100%');
      return false;
    }
    
    return true;
  };

  const handleUpdateField = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`/vitisad/put_campo/${fieldId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          nombre_campo: fieldName,
          porcentaje_arena: porcentajeArena,
          porcentaje_arcilla: porcentajeArcillaCalculado,
          porcentaje_limo: porcentajeLimo,
          profundidad_raices: profundidadRaices,
          potencial_hidrico_inicio_estres: potencialHidricoInicioEstres,
          potencial_hidrico_maximo_estres: potencialHidricoMaximoEstres,
          distancia_filas: distanciaFilas,
          distancia_plantas: distanciaPlantas,
          coeficiente_correccion_et0: coeficienteCorreccionEt0,
          k_escorrentia: kEscorrentia
        })
      });

      if (response.ok) {
        // NO poner setLoading(false) aquí, mantenerlo activo durante todo el proceso
        
        // Recargar los datos del campo actualizados desde el servidor
        const reloadFieldData = async () => {
          try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
              setLoading(false);
              navigate('/login');
              return;
            }

            const reloadResponse = await fetch('/vitisad/get_campos', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              }
            });

            if (reloadResponse.ok) {
              const data = await reloadResponse.json();
              if (data.data && Array.isArray(data.data)) {
                // Actualizar fieldsCache con los nuevos datos
                sessionStorage.setItem('fieldsCache', JSON.stringify(data.data));
                
                const updatedField = data.data.find(f => f.id.toString() === fieldId);
                if (updatedField) {
                  // Esperar un poco para que el modelo termine de recalcular
                  setTimeout(() => {
                    setLoading(false);
                    setShowDialog(false);
                    // Volver a la página del campo con los datos actualizados
                    navigate(`/field/${fieldId}`, { 
                      replace: true,
                      state: { field: updatedField }
                    });
                  }, 3000); // Dar más tiempo para que el modelo recalcule
                } else {
                  setLoading(false);
                  setShowDialog(false);
                  navigate(`/field/${fieldId}`, { state: { field } });
                }
              } else {
                setLoading(false);
                setShowDialog(false);
                navigate(`/field/${fieldId}`, { state: { field } });
              }
            } else {
              setLoading(false);
              setShowDialog(false);
              navigate(`/field/${fieldId}`, { state: { field } });
            }
          } catch (error) {
            console.error('Error recargando datos del campo:', error);
            setLoading(false);
            setShowDialog(false);
            navigate(`/field/${fieldId}`, { state: { field } });
          }
        };

        // Esperar un momento antes de recargar para que el backend empiece a procesar
        setTimeout(() => {
          reloadFieldData();
        }, 1000);
      } else if (response.status === 401) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
          handleUpdateField();
        } else {
          navigate('/login');
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || 'Error al actualizar el campo');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error actualizando campo:', error);
      setErrorMessage('Error de conexión al actualizar el campo');
      setLoading(false);
    }
  };

  const handleDeleteField = async () => {
    setDeleting(true);
    setErrorMessage('');

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`/vitisad/delete_campo/${fieldId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        // Recargar la lista de campos y actualizar fieldsCache
        const reloadResponse = await fetch('/vitisad/get_campos', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          if (data.data && Array.isArray(data.data)) {
            // Actualizar fieldsCache con los nuevos datos (sin el campo borrado)
            sessionStorage.setItem('fieldsCache', JSON.stringify(data.data));
          }
        }

        // Navegar a /fields
        navigate('/fields', { replace: true });
      } else if (response.status === 401) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
          handleDeleteField();
        } else {
          navigate('/login');
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || 'Error al eliminar el campo');
        setDeleting(false);
        setShowDeleteDialog(false);
      }
    } catch (error) {
      console.error('Error eliminando campo:', error);
      setErrorMessage('Error de conexión al eliminar el campo');
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar'
  });

  if (!field) {
    return (
      <PostLoginWrapper error={errorMessage} isEditField={true} loading={loading}>
        <div className="flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="loading"></div>
          <p style={{ color: 'black', marginTop: '20px' }}>Cargando datos del campo...</p>
        </div>
      </PostLoginWrapper>
    );
  }

  return (
    <PostLoginWrapper error={errorMessage} isEditField={true} loading={loading}>
      <div className="flex flex-column align-items-center justify-content-center w-full" style={{ padding: '2rem', minHeight: '70vh', position: 'relative' }}>
        <div className="w-full desktop-max-width" style={{ maxWidth: '800px' }}>
          <div className="grid">
            <div className="col-12 md:col-12" style={{ padding: '0 13px' }}>
              <div className="field mb-3">
                <label htmlFor="fieldName" className="block mb-2" style={{ color: 'black' }}>Nombre del campo:</label>
                <InputText 
                  id="fieldName"
                  className="w-full" 
                  placeholder="Nombre del campo" 
                  value={fieldName} 
                  onChange={(e) => setFieldName(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="porcentaje_arena" className="block mb-2" style={{ color: 'black' }}>Porcentaje de arena (%):</label>
                <InputNumber 
                  id="porcentaje_arena"
                  value={porcentajeArena} 
                  onValueChange={(e) => setPorcentajeArena(e.value)}
                  mode="decimal" 
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="porcentaje_arcilla" className="block mb-2" style={{ color: 'black' }}>Porcentaje de arcilla (%):</label>
                <InputNumber 
                  id="porcentaje_arcilla"
                  value={porcentajeArcillaCalculado} 
                  onValueChange={(e) => setPorcentajeArcilla(e.value)}
                  mode="decimal" 
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="porcentaje_limo" className="block mb-2" style={{ color: 'black' }}>Porcentaje de limo (%):</label>
                <InputNumber 
                  id="porcentaje_limo"
                  value={porcentajeLimo} 
                  onValueChange={(e) => setPorcentajeLimo(e.value)}
                  mode="decimal" 
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="profundidad_raices" className="block mb-2" style={{ color: 'black' }}>Profundidad de raíces (m):</label>
                <InputNumber 
                  id="profundidad_raices"
                  value={profundidadRaices} 
                  onValueChange={(e) => setProfundidadRaices(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="potencial_hidrico_inicio" className="block mb-2" style={{ color: 'black' }}>Pot. hídrico inicio estrés:</label>
                <InputNumber 
                  id="potencial_hidrico_inicio"
                  value={potencialHidricoInicioEstres} 
                  onValueChange={(e) => setPotencialHidricoInicioEstres(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="potencial_hidrico_maximo" className="block mb-2" style={{ color: 'black' }}>Pot. hídrico máximo estrés:</label>
                <InputNumber 
                  id="potencial_hidrico_maximo"
                  value={potencialHidricoMaximoEstres} 
                  onValueChange={(e) => setPotencialHidricoMaximoEstres(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="distancia_filas" className="block mb-2" style={{ color: 'black' }}>Distancia entre filas (m):</label>
                <InputNumber 
                  id="distancia_filas"
                  value={distanciaFilas} 
                  onValueChange={(e) => setDistanciaFilas(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="distancia_plantas" className="block mb-2" style={{ color: 'black' }}>Distancia entre plantas (m):</label>
                <InputNumber 
                  id="distancia_plantas"
                  value={distanciaPlantas} 
                  onValueChange={(e) => setDistanciaPlantas(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="coeficiente_correccion" className="block mb-2" style={{ color: 'black' }}>Coeficiente corrección ET0:</label>
                <InputNumber 
                  id="coeficiente_correccion"
                  value={coeficienteCorreccionEt0} 
                  onValueChange={(e) => setCoeficienteCorreccionEt0(e.value)}
                  mode="decimal" 
                  minFractionDigits={0}
                  maxFractionDigits={4}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
              <div className="field">
                <label htmlFor="k_escorrentia" className="block mb-2" style={{ color: 'black' }}>Coeficiente de escorrentía:</label>
                <InputNumber 
                  id="k_escorrentia"
                  value={kEscorrentia} 
                  onValueChange={(e) => setKEscorrentia(e.value)}
                  mode="decimal" 
                  minFractionDigits={1}
                  maxFractionDigits={2}
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="field-buttons flex justify-content-between align-items-center mt-4">
            <Button
              label="Eliminar campo" 
              icon="pi pi-trash" 
              className="p-button-danger delete-field-button edit-buttons"
              onClick={() => setShowDeleteDialog(true)}
              disabled={loading || deleting}
            />
            <div className="field-buttons div-edit-buttons flex gap-2">
              <Button 
                label="Cancelar" 
                icon="pi pi-times" 
                className="p-button-secondary edit-buttons"
                onClick={() => navigate(`/field/${fieldId}`, { state: { field } })}
                disabled={loading || deleting}
              />
              <Button 
                label={loading ? "Guardando..." : "Guardar Cambios"} 
                icon="pi pi-check" 
                className="edit-buttons"
                onClick={handleUpdateField}
                loading={loading}
                disabled={loading || deleting}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Campo actualizado"
        visible={showDialog}
        style={{ width: '400px' }}
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button label="OK" onClick={() => setShowDialog(false)} />
          </div>
        }
      >
        <p style={{ color: 'black' }}>
          El campo ha sido actualizado correctamente. El modelo se está recalculando con los nuevos parámetros.
        </p>
      </Dialog>

      <Dialog
        header="Confirmar eliminación"
        visible={showDeleteDialog}
        style={{ width: '400px' }}
        onHide={() => !deleting && setShowDeleteDialog(false)}
        footer={
          <div className="flex gap-2">
            <Button 
              label="Cancelar" 
              icon="pi pi-times" 
              className="p-button-secondary"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            />
            <Button 
              label={deleting ? "Eliminando..." : "Eliminar"} 
              icon="pi pi-trash" 
              className="p-button-danger"
              onClick={handleDeleteField}
              loading={deleting}
              disabled={deleting}
            />
          </div>
        }
      >
        <p style={{ color: 'black' }}>
          ¿Estás seguro de que deseas eliminar el campo "{fieldName}"? <br></br><br></br>Esta acción no se puede deshacer y se eliminarán todos los datos asociados (riegos, NDVI, datos climáticos, etc.).
        </p>
      </Dialog>
    </PostLoginWrapper>
  );
};

